import Phaser from "phaser";

import { api } from "../api";
import type { ActiveSession } from "../session";
import { showLeaderboard } from "../ui/LeaderboardOverlay";
import type { TouchControls } from "../ui/TouchControls";
import type { ScoreResult } from "../types";

/** Dados que a cidade passa para qualquer minigame. */
export interface MinigameData {
  /** Slug do jogo na tabela `games` — e o que amarra a pontuacao ao ranking. */
  game: string;
  label: string;
  session: ActiveSession;
  touch: TouchControls;
  onExit: () => void;
}

export type MinigamePhase = "ready" | "playing" | "dead";

/**
 * Fatia da sobra vertical que vai para cima do campo: o ceu aguenta crescer
 * muito mais que o chao, entao a maior parte sobe.
 */
const SKY_SHARE = 0.75;

/**
 * Altura do recorte do topo da tela (notch, ilha, barra de status) em pixels de
 * tela. `env()` so resolve dentro do CSS, entao medimos com um elemento sonda —
 * o canvas do jogo vai ate a borda por causa do `viewport-fit=cover`.
 */
function safeAreaTop(): number {
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;top:0;left:0;width:0;pointer-events:none;height:env(safe-area-inset-top,0px)";
  document.body.appendChild(probe);
  const height = probe.getBoundingClientRect().height;
  probe.remove();
  return height;
}

/** Texto do painel central: titulo/estado em cima, dica de controle embaixo. */
export interface PanelCopy {
  text: string;
  hint: string;
}

/**
 * Pedaco do mundo que a camera mostra, em pixels do campo. Cobre o campo
 * inteiro e sobra para os lados conforme a proporcao da tela: e essa sobra que
 * o cenario de cada jogo precisa preencher.
 */
export interface ViewRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
}

/**
 * Base dos minigames da cidade.
 *
 * Cuida do que todos tem em comum: enquadramento da tela, painel central,
 * ciclo pronto -> jogando -> morto, envio da pontuacao e ranking. Cada jogo
 * implementa apenas as regras da partida.
 *
 * ## Enquadramento
 *
 * O campo (`viewW` x `viewH`) e a area de jogo justa: e nele que estao as
 * medidas que definem a dificuldade, iguais para todo mundo, porque o ranking
 * e compartilhado. Ele sempre cabe inteiro na tela.
 *
 * A tela quase nunca tem a proporcao do campo, e o que sobra nao pode virar
 * borda preta: `this.view` diz qual retangulo do mundo esta visivel (sempre
 * maior ou igual ao campo) e cada jogo estica o cenario ate as bordas em
 * `layoutView()`. A sobra e cenario, nunca vantagem — veja `layoutView`.
 */
export abstract class MinigameScene extends Phaser.Scene {
  protected params!: MinigameData;
  protected phase: MinigamePhase = "ready";
  protected score = 0;

  /** Agrupa o cenario e os atores para controlar a ordem de desenho. */
  protected field!: Phaser.GameObjects.Container;
  protected scoreText!: Phaser.GameObjects.Text;

  /** Retangulo visivel do mundo, recalculado a cada resize/giro de tela. */
  protected view: ViewRect = {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    width: 0,
    height: 0,
    centerX: 0,
    centerY: 0,
  };

  private panel!: Phaser.GameObjects.Container;
  private panelText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;

  private runStartedAt = 0;
  private diedAt = 0;
  /** Cresce a cada morte: identifica a partida a que um envio pertence. */
  private runSeq = 0;
  private lastResult?: ScoreResult;
  /** Encadeia os envios para que uma partida rapida (morre -> reinicia -> morre)
   * nao derrube a pontuacao anterior que ainda esta a caminho do servidor. */
  private submitQueue: Promise<unknown> = Promise.resolve();
  protected overlayOpen = false;

  protected constructor(
    key: string,
    protected readonly viewW: number,
    protected readonly viewH: number,
  ) {
    super(key);
  }

  init(params: MinigameData): void {
    this.params = params;
    this.phase = "ready";
    this.score = 0;
    this.lastResult = undefined;
    this.overlayOpen = false;
  }

  create(): void {
    this.cameras.main.setBackgroundColor("#0d0e14");
    this.field = this.add.container(0, 0).setDepth(0);

    // O cenario ja nasce sabendo o tamanho da tela: `buildWorld` cria os
    // objetos e `layout` os estica ate as bordas.
    this.measureView();
    this.buildWorld();
    this.buildPanel();
    this.layout();

    this.resetRun();
    this.setScore(0);

    this.scale.on(Phaser.Scale.Events.RESIZE, this.layout, this);
    this.bindInput();

    const intro = this.introPanel();
    this.showPanel(intro.text, intro.hint);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off(Phaser.Scale.Events.RESIZE, this.layout, this);
    });
  }

  // ------------------------------------------------------------------ hooks

  /** Monta o cenario e os atores. Objetos que se movem vao para `this.field`. */
  protected abstract buildWorld(): void;

  /** Volta o jogo ao estado inicial (chamado antes da 1a partida e a cada retry). */
  protected abstract resetRun(): void;

  /** Um quadro de partida em andamento. Chame `this.fail()` ao perder. */
  protected abstract advance(dt: number): void;

  /** Comando do jogador durante a partida (voar, saltar...). */
  protected abstract action(pointer?: Phaser.Input.Pointer): void;

  /** Texto do painel de abertura. */
  protected abstract introPanel(): PanelCopy;

  /** Quadro fora da partida (animacao de espera, cenario parado...). */
  protected idle(_dt: number): void {}

  /**
   * Estica o cenario ate as bordas de `this.view` (chamado na criacao e a cada
   * resize/giro). Regra para nao mexer na dificuldade enquanto se preenche a
   * tela: sobra vertical e so cenario (o campo nao muda de altura), e sobra
   * horizontal anda junto com o jogador, que fica sempre a mesma distancia da
   * borda direita — a janela de reacao continua a mesma em qualquer tela.
   */
  protected layoutView(): void {}

  /** Fim do comando (soltar o dedo ou a tecla). */
  protected release(_pointer?: Phaser.Input.Pointer): void {}

  /** Como a pontuacao aparece no topo do campo. */
  protected formatScore(value: number): string {
    return String(value);
  }

  // ------------------------------------------------------------- montagem

  private buildPanel(): void {
    // As fontes sao dimensionadas em pixels do campo, nao da tela: a camera
    // aplica o zoom por cima e mantem tudo proporcional. Em telas estreitas o
    // zoom cai para fracoes de 1x (ver zoomFor), entao as fontes do painel
    // partem de um tamanho generoso para nao ficarem ilegiveis no celular
    // mesmo quando o zoom efetivo e baixo. A posicao vem de layoutHud().
    this.scoreText = this.add
      .text(this.viewW / 2, 8, "0", {
        fontFamily: '"Courier New", monospace',
        fontSize: "14px",
        color: "#ffffff",
        stroke: "#0d0e14",
        strokeThickness: 3,
        resolution: 4,
      })
      .setOrigin(0.5, 0)
      .setDepth(10);

    const panelWidth = 176;

    this.panelText = this.add
      .text(0, -18, "", {
        fontFamily: '"Courier New", monospace',
        fontSize: "11px",
        color: "#e8e9f0",
        align: "center",
        lineSpacing: 3,
        resolution: 4,
        wordWrap: { width: panelWidth - 16 },
      })
      .setOrigin(0.5, 0.5);

    this.hintText = this.add
      .text(0, 22, "", {
        fontFamily: '"Courier New", monospace',
        fontSize: "8px",
        color: "#8fd0e8",
        align: "center",
        lineSpacing: 3,
        resolution: 4,
        wordWrap: { width: panelWidth - 16 },
      })
      .setOrigin(0.5, 0.5);

    const box = this.add
      .rectangle(0, 0, panelWidth, 96, 0x0d0e14, 0.92)
      .setStrokeStyle(1, 0x0037b2);

    this.panel = this.add.container(0, 0, [box, this.panelText, this.hintText]).setDepth(20);
  }

  /**
   * Zoom da camera: o maior que ainda deixa o campo inteiro na tela, em passos
   * de 1/4. Arredondar para o inteiro abaixo desperdicaria tela demais (num
   * tablet, 2.95 viraria 2x e o jogo ficaria pequeno no meio do cenario);
   * 1/4 aproveita quase tudo e o pixel art continua sem borrao, porque a
   * filtragem e nearest. Para cima nunca: isso cortaria o campo.
   */
  private zoomFor(raw: number): number {
    return Math.max(0.25, Math.floor(raw * 4) / 4);
  }

  /**
   * Calcula o retangulo do mundo que a tela mostra. Ele cobre o campo inteiro e
   * ainda sobra em uma das direcoes (a tela do jogador quase nunca tem a mesma
   * proporcao do campo); a sobra vertical vai quase toda para o ceu.
   */
  private measureView(): number {
    const zoom = this.zoomFor(Math.min(this.scale.width / this.viewW, this.scale.height / this.viewH));

    // +2px de folga: com zoom fracionario a conta nao fecha em pixel cheio, e e
    // melhor cobrir um pouco a mais do que arriscar uma fresta preta na borda.
    const width = Math.ceil(this.scale.width / zoom) + 2;
    const height = Math.ceil(this.scale.height / zoom) + 2;
    const left = Math.floor((this.viewW - width) / 2);
    const top = Math.floor(-(height - this.viewH) * SKY_SHARE);

    this.view = {
      left,
      top,
      right: left + width,
      bottom: top + height,
      width,
      height,
      centerX: left + width / 2,
      centerY: top + height / 2,
    };
    return zoom;
  }

  /** Reenquadra a camera e manda o jogo esticar o cenario ate as bordas. */
  private layout(): void {
    const zoom = this.measureView();

    const camera = this.cameras.main;
    camera.setZoom(zoom);
    camera.centerOn(this.view.centerX, this.view.centerY);

    this.layoutView();
    this.layoutHud(zoom);
  }

  /**
   * Pontuacao no alto da tela e painel no centro dela, nao do campo. Agora que
   * o cenario vai ate a borda, o alto da tela pode estar embaixo do notch: a
   * pontuacao desce o que a safe area pedir.
   */
  private layoutHud(zoom: number): void {
    this.scoreText.setPosition(this.view.centerX, this.view.top + 8 + safeAreaTop() / zoom);
    this.panel.setPosition(this.view.centerX, this.view.centerY);
  }

  private bindInput(): void {
    const keyboard = this.input.keyboard!;
    keyboard.addCapture(["SPACE", "UP", "DOWN", "TAB"]);
    keyboard.on("keydown-SPACE", () => this.handleAction());
    keyboard.on("keydown-UP", () => this.handleAction());
    keyboard.on("keyup-SPACE", () => this.release());
    keyboard.on("keyup-UP", () => this.release());
    keyboard.on("keydown-ESC", () => this.exit());
    keyboard.on("keydown-TAB", (event: KeyboardEvent) => {
      event.preventDefault();
      this.openLeaderboard();
    });

    // No toque a area de arraste do analogico esta desligada, entao o toque
    // chega ao canvas e vira comando do minigame.
    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => this.handleAction(pointer));
    this.input.on("pointerup", (pointer: Phaser.Input.Pointer) => this.release(pointer));
    this.input.on("pointerupoutside", (pointer: Phaser.Input.Pointer) => this.release(pointer));
    this.params.touch.onBack(() => this.exit());
    this.params.touch.onLeaderboard(() => this.openLeaderboard());
  }

  // ------------------------------------------------------------- gameplay

  private handleAction(pointer?: Phaser.Input.Pointer): void {
    if (this.overlayOpen) return;

    switch (this.phase) {
      case "ready":
        this.startRun();
        this.action(pointer);
        break;
      case "playing":
        this.action(pointer);
        break;
      case "dead":
        // Pequena carencia para nao reiniciar sem querer no clique da morte.
        if (this.time.now - this.diedAt > 400) this.restart();
        break;
    }
  }

  private startRun(): void {
    this.phase = "playing";
    this.runStartedAt = this.time.now;
    this.hidePanel();
  }

  private restart(): void {
    this.resetRun();
    this.setScore(0);
    this.startRun();
  }

  override update(_time: number, delta: number): void {
    // Um travamento nao pode teleportar ninguem: o passo maximo e 50ms.
    const dt = Math.min(delta, 50) / 1000;
    if (this.phase === "playing") this.advance(dt);
    else this.idle(dt);
  }

  protected setScore(value: number): void {
    this.score = value;
    this.scoreText.setText(this.formatScore(value));
  }

  protected addScore(points = 1): void {
    this.setScore(this.score + points);
  }

  // ----------------------------------------------------------- fim de jogo

  /** Fim da partida: mostra o resultado e manda a pontuacao para o ranking. */
  protected async fail(): Promise<void> {
    this.phase = "dead";
    this.diedAt = this.time.now;
    const run = ++this.runSeq;
    const finalScore = this.score;

    this.cameras.main.shake(140, 0.006);
    this.showPanel(`FIM DE JOGO\n\nPontos: ${finalScore}`, "Enviando pontuação…");

    const result = await this.submitScore();

    // Se o jogador ja reiniciou enquanto a pontuacao era enviada, este painel
    // esta desatualizado e nao deve sobrepor a partida em andamento.
    if (this.phase !== "dead" || run !== this.runSeq) return;

    const lines = ["FIM DE JOGO", "", `Pontos: ${finalScore}`];
    if (result) {
      lines.push(result.isNewBest ? `NOVO RECORDE! ${result.rank}o lugar` : `Recorde: ${result.personalBest}`);
    }
    this.showPanel(
      lines.join("\n"),
      this.params.touch.enabled ? "Toque para jogar de novo" : "ESPAÇO joga de novo\nTAB ranking · ESC volta",
    );
  }

  private async submitScore(): Promise<ScoreResult | undefined> {
    const { session } = this.params;
    if (session.kind !== "player") return undefined;

    // Capturados agora: se um envio anterior ainda estiver em voo, este so
    // roda depois dele, mas com a pontuacao/duracao desta partida.
    const score = this.score;
    const duration = Math.round(this.time.now - this.runStartedAt);

    const task = this.submitQueue.then(() =>
      api.submitScore(this.params.game, session.session.token, score, duration).catch((error) => {
        console.warn("não foi possível enviar a pontuação", error);
        return undefined;
      }),
    );
    this.submitQueue = task;

    const result = await task;
    if (result) this.lastResult = result;
    return result;
  }

  private openLeaderboard(): void {
    if (this.overlayOpen) return;
    this.overlayOpen = true;

    const { session, touch } = this.params;
    touch.setMode("hidden");

    void showLeaderboard({
      slug: this.params.game,
      title: this.params.label,
      token: session.kind === "player" ? session.session.token : undefined,
      currentUserId: session.kind === "player" ? session.session.user.id : undefined,
      result: this.lastResult,
    }).then(() => {
      this.overlayOpen = false;
      touch.setMode("minigame");
    });
  }

  protected showPanel(text: string, hint: string): void {
    this.panelText.setText(text);
    this.hintText.setText(hint);
    this.panel.setVisible(true);
  }

  protected hidePanel(): void {
    this.panel.setVisible(false);
  }

  private exit(): void {
    const onExit = this.params.onExit;
    this.scene.stop();
    onExit();
  }
}
